import React from 'react';
import {
	FormattedMessage,
	defineMessages,
} from 'react-intl';

import AuthLogo from '~/components/atoms/auth/AuthLogo';
import AuthScreenLayout from '~/components/atoms/auth/AuthScreenLayout';
import AuthTitle from '~/components/atoms/auth/AuthTitle';

import useAuthenticatedSession from '~/hooks/useAuthenticatedSession';

import {
	_refreshToken,
} from '~/utilities/sessionRefreshUtil';



const messages = defineMessages({
	title: {
		id: 'ui.conductorAuthentication.title',
	},
});



const ConductorAuthenticationPage: React.FC = () => {
	// Initialize as true to skip query on first render
	// This ensures refresh happens before query runs (prevents race condition)
	const [isRefreshingOnMount, setIsRefreshingOnMount] = React.useState(true);
	// Use ref instead of state to prevent effect re-run loop
	const hasAttemptedRefreshRef = React.useRef(false);

	// Attempt token refresh on mount (before authentication check)
	// This handles the case where user closed tab and reopened after token expiration
	React.useEffect(
		() => {
			if (hasAttemptedRefreshRef.current) {
				return;
			}

			hasAttemptedRefreshRef.current = true;

			let isMounted = true;

			// Add timeout fallback to ensure query runs even if refresh hangs
			const timeoutId = setTimeout(
				() => {
					if (isMounted) {
						setIsRefreshingOnMount(false);
					}
				},
				10_000, // 10 second timeout
			);

			_refreshToken()
				.finally(() => {
					clearTimeout(timeoutId);
					if (isMounted) {
						setIsRefreshingOnMount(false);
					}
				});

			return () => {
				isMounted = false;
				clearTimeout(timeoutId);
			};
		},
		[], // Empty dependency array - run once on mount
	);

	// Skip GraphQL query until refresh attempt completes (prevents race condition)
	useAuthenticatedSession({
		allowStart: true,
		skip: isRefreshingOnMount,
	});

	return (
		<AuthScreenLayout
			logo={(
				<AuthLogo />
			)}
		>
			<AuthTitle>
				<FormattedMessage {...messages.title} />
			</AuthTitle>
		</AuthScreenLayout>
	);
};



export default ConductorAuthenticationPage;
